/**
 * AddColumnControl — the schema builder's "+ Add column" affordance (list-
 * foot primary action, ConfigListCard's "+ Add {noun}" tonal-button idiom).
 * Opens a keyboard-navigable dropdown (NotificationBell's click-away/Escape/
 * role="menu" idiom, reused verbatim) with two groups: SYSTEM fields not
 * yet present in the schema (adds immediately on click) and "Create custom
 * field…" (opens the FieldEditorModal in custom-create mode via
 * onCreateCustom). A system ref counts as "present" whether its row is
 * active OR archived — an archived system field is restored via the
 * Archived-columns disclosure, never re-created (re-creating it would 422
 * FIELD_DUPLICATE server-side, since BacklogFieldValidator's per-project
 * uniqueness check does not exempt archived rows). SCHED is additionally
 * excluded from the palette while the schema is scheduling-ineligible (A9)
 * — see isSchedulingEligible.
 */
/* eslint-disable react-refresh/only-export-components --
   this module exports the SYSTEM_FIELD_DEFAULT_LABEL lookup alongside the
   component so other schema-builder surfaces (Task 4+) share one source of
   truth for default labels; losing Fast Refresh here is accepted (ProjectForm
   precedent). */
import { useEffect, useRef, useState } from 'react';
import { btnTonalStyle } from '@/components/ProjectForm';
import { isSchedulingEligible } from '@/api/schedulingEligibility';
import { BACKLOG_FIELD_MANDATORY_TRIO } from '@/types/backlogField';
import type { BacklogSystemField } from '@/types/backlogField';
import type { BuilderField } from './FieldBuilderRow';

/** Default display label seeded for a system field added from the palette —
 *  matches the exact wording the V42 mirror-schema backfill uses for every
 *  ref it covers (manage-api/src/main/resources/db/migration/V42__backlog_schema.sql);
 *  WSJF/MILESTONE/REMARKS aren't in that backfill's visible set but still
 *  need a sensible default since they remain selectable from the palette. */
export const SYSTEM_FIELD_DEFAULT_LABEL: Record<BacklogSystemField, string> = {
  RECORD_ID: 'Record ID',
  DATE_FROM: 'From',
  DATE_TO: 'To',
  EXTERNAL_REF: 'CR Ref',
  IM_REF: 'IM Ref',
  DESCRIPTION: 'Description',
  PRIORITY: 'Priority',
  STATUS: 'Status',
  EFFORT_MD: 'Effort (MD)',
  WSJF: 'WSJF',
  OWNER: 'Owner',
  DEVELOPER: 'Developer',
  SCHED: 'Sched',
  TOTAL_EFFORT_DAYS: 'Total Effort (Days)',
  TASKS: 'Tasks',
  RAID_LINKS: 'RAIDs',
  MILESTONE: 'Milestone',
  REMARKS: 'Remarks',
};

const ALL_SYSTEM_FIELDS = Object.keys(SYSTEM_FIELD_DEFAULT_LABEL) as BacklogSystemField[];

export interface AddColumnControlProps {
  /** The schema's current fields (active + archived). */
  existing: BuilderField[];
  onAddSystem: (ref: BacklogSystemField, defaultLabel: string) => void;
  onCreateCustom: () => void;
  disabled?: boolean;
}

export function AddColumnControl({ existing, onAddSystem, onCreateCustom, disabled }: AddColumnControlProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const presentRefs = new Set(
    existing.map((f) => f.systemRef).filter((r): r is BacklogSystemField => r !== null),
  );
  // A9: SCHED is a read-only computed field that only means something once the schema is
  // scheduling-eligible (a non-archived Effort field plus at least one of Owner/Developer)
  // — offering it earlier just invites a column nobody can ever populate.
  const schedulingEligible = isSchedulingEligible(existing);
  const availableSystemFields = ALL_SYSTEM_FIELDS.filter(
    (ref) => !BACKLOG_FIELD_MANDATORY_TRIO.includes(ref) && !presentRefs.has(ref)
      && (ref !== 'SCHED' || schedulingEligible),
  );

  function handleAddSystem(ref: BacklogSystemField) {
    onAddSystem(ref, SYSTEM_FIELD_DEFAULT_LABEL[ref]);
    setOpen(false);
  }

  function handleCreateCustom() {
    onCreateCustom();
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="pressable focus-ring"
        style={{ ...btnTonalStyle, opacity: disabled ? 0.4 : 1 }}
      >
        + Add column
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Add column"
          className="pop-in"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 260, maxHeight: 340, overflowY: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)', zIndex: 30, padding: 6,
            ['--pop-origin' as string]: 'top left',
          }}
        >
          {availableSystemFields.length > 0 && (
            <div
              style={{
                padding: '6px 8px 4px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)',
                fontWeight: 600, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}
            >
              System fields
            </div>
          )}
          {availableSystemFields.map((ref) => (
            <button
              key={ref}
              type="button"
              role="menuitem"
              onClick={() => handleAddSystem(ref)}
              className="focus-ring"
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none',
                borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {SYSTEM_FIELD_DEFAULT_LABEL[ref]}
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          <button
            type="button"
            role="menuitem"
            onClick={handleCreateCustom}
            className="focus-ring"
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none',
              borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer',
              fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--brand)',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            + Create custom field…
          </button>
        </div>
      )}
    </div>
  );
}
